import { useEffect } from 'react';
import { useLatest } from 'ahooks';
import useReportDeletedCheck from './useReportDeletedCheck';
import { getFileData, generatePDFPreviewUrl } from '../utils';

import type { IPDFPreviewDownloadProps } from '../types';

interface IUsePDFUrlEffectProps extends Omit<IPDFPreviewDownloadProps, 'resourceName'> {
  // 是否可执行该hooks内逻辑，仅非COS预览时执行该hooks内逻辑
  executable?: boolean;
  systemId: string;
  studioId?: string;
  resourceId?: string;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setIsError: React.Dispatch<React.SetStateAction<boolean>>;
  setFileSuffix: React.Dispatch<React.SetStateAction<string>>;
  setUrl: React.Dispatch<React.SetStateAction<string>>;
  setPreviewUrl: React.Dispatch<React.SetStateAction<string>>;
  // 加载成功回调
  onSuccess?: (payload: { url: string; previewUrl: string }) => void;
  // 加载失败回调
  // REMARK: 这里可以扩展加载异常的处理逻辑
  onError?: (exception: unknown) => void;
  // 报告被删除回调
  onDeleted?: () => void;
}

export default function usePDFUrlEffect(props: IUsePDFUrlEffectProps) {
  const {
    executable = false,
    systemId,
    studioId = '',
    resourceId = '',
    fetchPDFUrl,
    setIsLoading,
    setIsError,
    setFileSuffix,
    setUrl,
    setPreviewUrl,
    onSuccess,
    onError,
    onDeleted,
  } = props;

  const fetchPDFUrlLatest = useLatest(fetchPDFUrl);
  const { checkReportDeleted } = useReportDeletedCheck({ executable, systemId, studioId, resourceId });

  const updateUrl = async () => {
    if (!executable) return;
    // 前置检测报告是否被删除
    const isDeleted = await checkReportDeleted();
    if (isDeleted) {
      onDeleted?.();
      return;
    }
    try {
      setIsLoading(true);
      const res = await fetchPDFUrlLatest.current?.();
      const { url, suffix } = getFileData(res);
      const previewUrl = generatePDFPreviewUrl(url);
      setFileSuffix(suffix);
      setUrl(url);
      setPreviewUrl(previewUrl);
      setIsError(false);
      setIsLoading(false);
      onSuccess?.({ url, previewUrl });
    } catch (exception: unknown) {
      let isError = true;
      try {
        const decoder = new TextDecoder('utf-8');
        const exceptionJSONString = decoder.decode(exception as any);
        const a = JSON.parse(exceptionJSONString);
        if (a.code === 4001) {
          isError = false;
        }
      } catch (e) {
        console.error(e);
      }
      if (isError) {
        setIsError(true);
        setIsLoading(false);
        onError?.(exception);
      }
    }
  };

  const handleReload = (e: React.SyntheticEvent<HTMLElement>) => {
    e?.stopPropagation();
    e?.preventDefault();
    if (!executable) return;
    updateUrl();
  };

  useEffect(() => {
    if (!executable) return;

    updateUrl();
  }, [executable]);

  return { onReload: handleReload };
}
